import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, BarChart3, CreditCard, Hexagon, Mail, FileText, MessageSquare,
  CalendarClock, Bell, BellRing, Key, Settings, UserCog, Activity, LifeBuoy, Download,
  Shield, X, LogOut, GitBranch, Globe, ClipboardList, Scale } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

// Shared red-themed superadmin navigation. Used by both the cockpit
// (SuperadminPage) and the customer detail page so the nav can never drift
// between the two.
//
// Props:
//   activeSection    – highlights the matching item (null on the detail page)
//   onSectionClick   – when provided, section items are BUTTONS calling it
//                      (cockpit mode). When omitted, section items are LINKS
//                      to /superadmin?section=<section> (detail-page mode).
//   sidebarOpen / setSidebarOpen – mobile off-canvas toggle
//   supportOpenCount – optional badge count on the Support item
// THE nav list. Exported because SuperadminPage also needs it (to pick which section
// to open by default) and it used to keep its own copy — which silently went stale
// twice: once when Trials was added, once when Email Reminders was. One list, one
// place. If you add a section, add it here and nowhere else.
export const buildSuperadminNav = (user) => [
    { icon: <Building2 className="w-5 h-5" />, label: 'Customers', section: 'customers' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Interest list', section: 'interest' },
    { icon: <CalendarClock className="w-5 h-5" />, label: 'Trials', section: 'trials' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', section: 'analytics' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Billing', section: 'billing' },
    { icon: <Hexagon className="w-5 h-5" />, label: 'Company Details', section: 'company' },
    { icon: <Mail className="w-5 h-5" />, label: 'Email Config', section: 'email' },
    { icon: <FileText className="w-5 h-5" />, label: 'Email Templates', section: 'emailtemplates' },
    { icon: <MessageSquare className="w-5 h-5" />, label: 'Messages', section: 'messages' },
    { icon: <BellRing className="w-5 h-5" />, label: 'Email Reminders', section: 'approvalreminders' },
    { icon: <GitBranch className="w-5 h-5" />, label: 'Request Routing', section: 'requestrouting' },
    { icon: <Globe className="w-5 h-5" />, label: 'Public Holidays', section: 'publicholidays' },
    { icon: <CalendarClock className="w-5 h-5" />, label: 'Rollover', section: 'rollover' },
    // Read-only, any superadmin — it is public law, not customer data (21/10/2026).
    { icon: <Scale className="w-5 h-5" />, label: 'Employment law', section: 'employmentlaw' },
    { icon: <Bell className="w-5 h-5" />, label: 'Notifications', section: 'notifications' },
    { icon: <Key className="w-5 h-5" />, label: 'API Keys', section: 'api' },
    { icon: <Settings className="w-5 h-5" />, label: 'Platform Settings', section: 'settings' },
    ...(user?.is_platform_owner ? [{ icon: <UserCog className="w-5 h-5" />, label: 'Platform Team', section: 'team' }] : []),
    { icon: <Activity className="w-5 h-5" />, label: 'Activity Log', section: 'activity', link: '/superadmin/activity' },
    { icon: <LifeBuoy className="w-5 h-5" />, label: 'Support', section: 'support' },
    { icon: <Download className="w-5 h-5" />, label: 'Data Export', section: 'export' },
];

const SuperadminSidebar = ({
  activeSection = null,
  onSectionClick = null,
  sidebarOpen = false,
  setSidebarOpen = () => {},
  supportOpenCount = 0,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const adminNavItems = buildSuperadminNav(user);

  // Capability gating (Phase 2): owners see all; others only their granted areas.
  const superCaps = user?.platform_capabilities || [];
  const isOwnerSA = !!user?.is_platform_owner;
  const SECTION_CAP = {
    customers: 'customers', trials: 'customers', analytics: 'analytics', billing: 'billing', company: 'company',
    email: 'email', emailtemplates: 'email', approvalreminders: 'email',
    messages: 'messages', rollover: 'rollover', notifications: 'notifications',
    api: 'api_keys', settings: 'platform_settings', activity: 'activity_log',
    export: 'data_export', support: 'support',
  };
  const canSeeSection = (section) => {
    if (isOwnerSA) return true;
    const cap = SECTION_CAP[section];
    return !cap || superCaps.includes(cap);
  };
  const visibleNavItems = adminNavItems.filter((item) => canSeeSection(item.section));

  const itemClasses = (isActive) =>
    `w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${
      isActive
        ? 'bg-red-500/10 text-red-500'
        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
    }`;

  return (
    <>
      <aside className={`fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50 w-64 bg-card border-r border-red-500/20 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Admin Logo */}
          <div className="p-6 border-b border-red-500/20 bg-red-500/5">
            <div className="flex items-center justify-between">
              <Link to="/superadmin" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <span className="font-heading font-bold text-lg">HolidayHive</span>
                  <p className="text-xs text-red-500 font-medium">ADMIN PANEL</p>
                </div>
              </Link>
              <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Admin Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item) => {
              const isActive = activeSection === item.section;
              if (item.link) {
                return (
                  <Link key={item.section} to={item.link} className={itemClasses(isActive)}>
                    {item.icon}
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              }
              if (onSectionClick) {
                return (
                  <button
                    key={item.section}
                    onClick={() => { onSectionClick(item.section); setSidebarOpen(false); }}
                    className={itemClasses(isActive)}
                  >
                    {item.icon}
                    <span className="font-medium flex-1">{item.label}</span>
                    {item.section === 'support' && supportOpenCount > 0 && (
                      <span className="text-xs font-semibold bg-red-500 text-white rounded-full px-2 py-0.5">
                        {supportOpenCount}
                      </span>
                    )}
                  </button>
                );
              }
              // Detail-page mode: jump back to the cockpit on the chosen section.
              return (
                <Link
                  key={item.section}
                  to={`/superadmin?section=${item.section}`}
                  className={itemClasses(isActive)}
                >
                  {item.icon}
                  <span className="font-medium flex-1">{item.label}</span>
                  {item.section === 'support' && supportOpenCount > 0 && (
                    <span className="text-xs font-semibold bg-red-500 text-white rounded-full px-2 py-0.5">
                      {supportOpenCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Admin User */}
          <div className="p-4 border-t border-red-500/20 bg-red-500/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-red-500 font-medium">SUPERADMIN</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default SuperadminSidebar;
